import { useEffect, useState } from "react";
import { StickyNote, FileDown } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import api, { fmtNum } from "../lib/api";

const MONTHS_HR = ["sij", "vlj", "ožu", "tra", "svi", "lip", "srp", "kol", "ruj", "lis", "stu", "pro"];
const MONTHS_FULL = ["siječanj", "veljača", "ožujak", "travanj", "svibanj", "lipanj", "srpanj", "kolovoz", "rujan", "listopad", "studeni", "prosinac"];
const BAR_LABELS = { actual_in: "Priljev (ostv.)", actual_out: "Odljev (ostv.)", planned_in: "Priljev (plan)", planned_out: "Odljev (plan)" };

const yearOptions = () => {
  const y = new Date().getFullYear();
  return [y + 1, y, y - 1, y - 2, y - 3];
};

export default function AnnualPage() {
  const [year, setYear] = useState(String(new Date().getFullYear()));
  const [data, setData] = useState(null);
  const [notes, setNotes] = useState({});

  useEffect(() => {
    api.get("/reports/annual", { params: { year } }).then((r) => setData(r.data));
    api.get("/month-notes", { params: { year } }).then((r) => {
      const map = {};
      r.data.forEach((n) => { map[n.month] = n.text; });
      setNotes(map);
    });
  }, [year]);

  const chartData = data?.months.map((m, i) => ({ ...m, label: MONTHS_HR[i] })) || [];
  const hasPlanned = data && (data.totals.planned_in > 0 || data.totals.planned_out > 0);

  return (
    <div className="space-y-6 fade-up" data-testid="annual-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] font-semibold text-[#64748B] mb-1">Plan i ostvarenje</p>
          <h1 className="font-heading text-4xl font-bold tracking-tight text-[#0F172A]">Godišnji pregled</h1>
        </div>
        <div className="flex gap-2">
          <Select value={year} onValueChange={setYear}>
            <SelectTrigger data-testid="annual-year-select" className="w-32 bg-white"><SelectValue /></SelectTrigger>
            <SelectContent>
              {yearOptions().map((y) => <SelectItem key={y} value={String(y)}>{y}.</SelectItem>)}
            </SelectContent>
          </Select>
          <button
            data-testid="annual-export-btn"
            onClick={async () => {
              const r = await api.get("/export/annual", { params: { year }, responseType: "blob" });
              const url = URL.createObjectURL(r.data);
              const a = document.createElement("a");
              a.href = url;
              a.download = `vtz-cashflow-${year}.xlsx`;
              a.click();
              URL.revokeObjectURL(url);
            }}
            className="inline-flex items-center gap-2 h-10 px-4 rounded-sm bg-[#0D9488] hover:bg-[#0F766E] text-white text-sm font-medium transition-colors duration-200"
          >
            <FileDown className="w-4 h-4" /> Izvoz godine (Excel)
          </button>
        </div>
      </div>

      {data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="bg-white border border-[#E2E8F0] rounded-sm p-5" data-testid="annual-in-card">
              <p className="text-xs uppercase tracking-[0.15em] font-semibold text-[#64748B] mb-2">Ostvareni priljev</p>
              <p className="font-heading text-2xl font-bold text-[#0D9488] tabular-nums">+{fmtNum(data.totals.actual_in, "€")}</p>
            </div>
            <div className="bg-white border border-[#E2E8F0] rounded-sm p-5" data-testid="annual-out-card">
              <p className="text-xs uppercase tracking-[0.15em] font-semibold text-[#64748B] mb-2">Ostvareni odljev</p>
              <p className="font-heading text-2xl font-bold text-[#DC2626] tabular-nums">−{fmtNum(data.totals.actual_out, "€")}</p>
            </div>
            <div className="bg-white border border-[#E2E8F0] rounded-sm p-5" data-testid="annual-net-card">
              <p className="text-xs uppercase tracking-[0.15em] font-semibold text-[#64748B] mb-2">Ostvareni neto</p>
              <p className={`font-heading text-2xl font-bold tabular-nums ${data.totals.actual_net >= 0 ? "text-[#0D9488]" : "text-[#DC2626]"}`}>{fmtNum(data.totals.actual_net, "€")}</p>
            </div>
            <div className="bg-white border border-dashed border-[#D97706] rounded-sm p-5" data-testid="annual-planned-card">
              <p className="text-xs uppercase tracking-[0.15em] font-semibold text-[#D97706] mb-2">Planirani neto (otvoreno)</p>
              <p className="font-heading text-2xl font-bold text-[#D97706] tabular-nums">{fmtNum(data.totals.planned_net, "€")}</p>
            </div>
          </div>

          <div className="bg-white border border-[#E2E8F0] rounded-sm p-5">
            <h2 className="font-heading text-lg font-semibold text-[#0F172A] mb-4">Po mjesecima (EUR)</h2>
            <div className="h-72">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} barGap={2}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" vertical={false} />
                  <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#475569" }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: "#475569" }} axisLine={false} tickLine={false} width={80} />
                  <Tooltip formatter={(v, n) => [fmtNum(v, "€"), BAR_LABELS[n] || n]} cursor={{ fill: "#F1F5F9" }} labelFormatter={(l) => `${MONTHS_FULL[MONTHS_HR.indexOf(l)]} ${year}.`} />
                  <Legend formatter={(v) => <span className="text-xs text-[#475569]">{BAR_LABELS[v] || v}</span>} />
                  <Bar dataKey="actual_in" fill="#0D9488" radius={[2, 2, 0, 0]} />
                  <Bar dataKey="actual_out" fill="#DC2626" radius={[2, 2, 0, 0]} />
                  {hasPlanned && <Bar dataKey="planned_in" fill="#0D9488" fillOpacity={0.35} radius={[2, 2, 0, 0]} />}
                  {hasPlanned && <Bar dataKey="planned_out" fill="#DC2626" fillOpacity={0.35} radius={[2, 2, 0, 0]} />}
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>

          <div className="bg-white border border-[#E2E8F0] rounded-sm overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-[#F1F5F9] hover:bg-[#F1F5F9]">
                  <TableHead>Mjesec</TableHead>
                  <TableHead className="text-right">Priljev €</TableHead>
                  <TableHead className="text-right">Odljev €</TableHead>
                  <TableHead className="text-right border-r border-[#E2E8F0]">Neto €</TableHead>
                  <TableHead className="text-right text-[#D97706]">Plan priljev €</TableHead>
                  <TableHead className="text-right text-[#D97706]">Plan odljev €</TableHead>
                  <TableHead className="text-right text-[#D97706]">Plan neto €</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody data-testid="annual-table-body">
                {data.months.map((m, i) => {
                  const emptyRow = !m.actual_in && !m.actual_out && !m.planned_in && !m.planned_out;
                  return (
                    <TableRow key={m.month} className={`hover:bg-[#F8FAFC] transition-colors duration-200 ${emptyRow ? "text-[#64748B]" : ""}`} data-testid={`annual-row-${m.month}`}>
                      <TableCell className="text-sm font-medium">
                        <span className="capitalize">{MONTHS_FULL[i]}</span>
                        {notes[m.month] && (
                          <span className="flex items-start gap-1 mt-1 text-xs font-normal italic text-[#D97706] max-w-[220px]" data-testid={`month-note-${m.month}`}>
                            <StickyNote className="w-3 h-3 shrink-0 mt-0.5" /> {notes[m.month]}
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-right tabular-nums text-sm text-[#0D9488]">{m.actual_in ? `+${fmtNum(m.actual_in)}` : "—"}</TableCell>
                      <TableCell className="text-right tabular-nums text-sm text-[#DC2626]">{m.actual_out ? `−${fmtNum(m.actual_out)}` : "—"}</TableCell>
                      <TableCell className={`text-right tabular-nums text-sm font-semibold border-r border-[#E2E8F0] ${m.actual_net >= 0 ? "text-[#0D9488]" : "text-[#DC2626]"}`}>{m.actual_in || m.actual_out ? fmtNum(m.actual_net) : "—"}</TableCell>
                      <TableCell className="text-right tabular-nums text-sm text-[#D97706]">{m.planned_in ? `+${fmtNum(m.planned_in)}` : "—"}</TableCell>
                      <TableCell className="text-right tabular-nums text-sm text-[#D97706]">{m.planned_out ? `−${fmtNum(m.planned_out)}` : "—"}</TableCell>
                      <TableCell className="text-right tabular-nums text-sm text-[#D97706] font-medium">{m.planned_in || m.planned_out ? fmtNum(m.planned_net) : "—"}</TableCell>
                    </TableRow>
                  );
                })}
                <TableRow className="bg-[#F1F5F9] hover:bg-[#F1F5F9] font-semibold" data-testid="annual-totals-row">
                  <TableCell className="text-sm">Ukupno {year}.</TableCell>
                  <TableCell className="text-right tabular-nums text-sm text-[#0D9488]">+{fmtNum(data.totals.actual_in)}</TableCell>
                  <TableCell className="text-right tabular-nums text-sm text-[#DC2626]">−{fmtNum(data.totals.actual_out)}</TableCell>
                  <TableCell className={`text-right tabular-nums text-sm border-r border-[#E2E8F0] ${data.totals.actual_net >= 0 ? "text-[#0D9488]" : "text-[#DC2626]"}`}>{fmtNum(data.totals.actual_net)}</TableCell>
                  <TableCell className="text-right tabular-nums text-sm text-[#D97706]">+{fmtNum(data.totals.planned_in)}</TableCell>
                  <TableCell className="text-right tabular-nums text-sm text-[#D97706]">−{fmtNum(data.totals.planned_out)}</TableCell>
                  <TableCell className="text-right tabular-nums text-sm text-[#D97706]">{fmtNum(data.totals.planned_net)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </div>
        </>
      )}
    </div>
  );
}
